import axios from 'axios';
import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { urlFichier } from '@/lib/fichiers';
import {
  Plus, Trash2, ChevronRight, ChevronDown, Upload, X, FileText,
  Image as ImageIcon, CheckCircle, Clock, AlertCircle, Circle,
  CalendarDays, User, DollarSign, Building2, Edit3, ArrowLeft,
  Loader2, Paperclip, Star, HelpCircle
} from "lucide-react";

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const STATUTS_PROJET = {
  en_preparation:    { label: "En préparation",     color: "bg-blue-100 text-blue-800" },
  soumission_envoyee:{ label: "Soumission envoyée", color: "bg-amber-100 text-amber-800" },
  gagne:             { label: "Gagné",              color: "bg-green-100 text-green-800" },
  perdu:             { label: "Perdu",              color: "bg-red-100 text-red-800" },
  en_attente:        { label: "En attente",         color: "bg-slate-100 text-slate-700" },
};

const STATUTS_ETAPE = {
  a_faire:  { label: "À faire",    icon: Circle,       color: "text-slate-400" },
  en_cours: { label: "En cours",   icon: Clock,        color: "text-blue-500" },
  complete: { label: "Complété",   icon: CheckCircle,  color: "text-green-500" },
  bloque:   { label: "Bloqué",     icon: AlertCircle,  color: "text-red-500" },
};

function fmt$(n) {
  if (!n) return "—";
  return new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(n);
}

// ── ProjetCard ────────────────────────────────────────────────────────────────
function ProjetCard({ projet, onClick, onDelete }) {
  const st = STATUTS_PROJET[projet.statut] || STATUTS_PROJET.en_preparation;
  const done = (projet.etapes || []).filter(e => e.statut === "complete").length;
  const total = (projet.etapes || []).length;
  return (
    <Card className="cursor-pointer hover:shadow-md transition-all border-l-4 border-l-indigo-500"
      onClick={() => onClick(projet)} data-testid={`projet-card-${projet.id}`}>
      <CardContent className="p-4">
        <div className="flex justify-between items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Star className="w-4 h-4 text-indigo-500 flex-shrink-0" />
              <h3 className="font-semibold text-slate-800 dark:text-slate-100 truncate">{projet.titre}</h3>
            </div>
            {projet.client_nom && (
              <p className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                <Building2 className="w-3 h-3" /> {projet.client_nom}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <Badge className={`text-xs ${st.color}`}>{st.label}</Badge>
              {projet.valeur_estimee > 0 && (
                <span className="text-xs text-slate-500 flex items-center gap-0.5">
                  <DollarSign className="w-3 h-3" />{fmt$(projet.valeur_estimee)}
                </span>
              )}
              {total > 0 && (
                <span className="text-xs text-slate-400">{done}/{total} étapes</span>
              )}
            </div>
          </div>
          <button className="text-slate-300 hover:text-red-400 transition-colors p-1 flex-shrink-0"
            onClick={e => { e.stopPropagation(); onDelete(projet.id); }}
            data-testid={`delete-projet-${projet.id}`}>
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── EtapeRow ──────────────────────────────────────────────────────────────────
function EtapeRow({ etape, onUpdate, onDelete, adminEmployees }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...etape });
  const St = STATUTS_ETAPE[etape.statut] || STATUTS_ETAPE.a_faire;
  const Icon = St.icon;

  const save = () => { onUpdate(form); setEditing(false); };

  return (
    <div className="border rounded-lg p-3 bg-white dark:bg-slate-800 space-y-2">
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${St.color}`} />
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="space-y-2">
              <Input value={form.titre} onChange={e => setForm(f => ({...f, titre: e.target.value}))}
                placeholder="Titre de l'étape" className="text-sm" data-testid="etape-titre-input" />
              <Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                placeholder="Description..." rows={2} className="text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Responsable</Label>
                  <Select value={form.responsable_id || ''} onValueChange={v => {
                    const emp = (adminEmployees || []).find(e => e.id === v);
                    setForm(f => ({...f, responsable_id: v, responsable_nom: emp ? `${emp.prenom} ${emp.nom}` : ''}));
                  }}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Assigner..." /></SelectTrigger>
                    <SelectContent>
                      {(adminEmployees || []).map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.prenom} {e.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Échéance</Label>
                  <Input type="date" value={form.echeance}
                    onChange={e => setForm(f => ({...f, echeance: e.target.value}))} className="text-sm" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Statut</Label>
                <Select value={form.statut} onValueChange={v => setForm(f => ({...f, statut: v}))}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUTS_ETAPE).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))}
                placeholder="Notes additionnelles..." rows={2} className="text-sm" />
              <div className="flex gap-2">
                <Button size="sm" onClick={save} data-testid="save-etape-btn">Sauvegarder</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Annuler</Button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium text-sm text-slate-800 dark:text-slate-100">{etape.titre || "Sans titre"}</p>
                <div className="flex items-center gap-1">
                  <button onClick={() => setEditing(true)} className="text-slate-400 hover:text-slate-600 p-0.5"
                    data-testid="edit-etape-btn"><Edit3 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => onDelete(etape.id)} className="text-slate-300 hover:text-red-400 p-0.5">
                    <Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              {etape.description && <p className="text-xs text-slate-500 mt-0.5">{etape.description}</p>}
              <div className="flex flex-wrap gap-2 mt-1">
                <Badge className={`text-[10px] ${STATUTS_ETAPE[etape.statut]?.color || ''}`}>
                  {STATUTS_ETAPE[etape.statut]?.label}
                </Badge>
                {etape.responsable_nom && (
                  <span className="text-[10px] text-slate-500 flex items-center gap-0.5">
                    <User className="w-3 h-3" />{etape.responsable_nom}
                  </span>
                )}
                {etape.echeance && (() => {
                  const days = Math.ceil((new Date(etape.echeance) - new Date()) / 86400000);
                  const urgColor = days < 0 ? 'text-red-600 font-bold' : days <= 3 ? 'text-amber-600' : 'text-slate-500';
                  return (
                    <span className={`text-[10px] flex items-center gap-0.5 ${urgColor}`}>
                      <CalendarDays className="w-3 h-3" />{etape.echeance}{days < 0 ? ' (en retard!)' : days <= 3 ? ' (bientôt)' : ''}
                    </span>
                  );
                })()}
              </div>
              {etape.notes && <p className="text-[10px] text-slate-400 italic mt-1 border-l-2 border-slate-200 pl-2">{etape.notes}</p>}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── ProjetDetail ──────────────────────────────────────────────────────────────
function ProjetDetail({ projet: initialProjet, onBack, token }) {
  const [projet, setProjet] = useState(initialProjet);
  const [saving, setSaving] = useState(false);
  const [editInfo, setEditInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({
    titre: projet.titre,
    client_nom: projet.client_nom || "",
    adresse: projet.adresse || "",
    valeur_estimee: projet.valeur_estimee || 0,
    statut: projet.statut || "en_preparation",
    notes_generales: projet.notes_generales || "",
  });
  const [newEtape, setNewEtape] = useState(false);
  const [etapeForm, setEtapeForm] = useState({ titre: "", description: "", responsable_nom: "", responsable_id: "", echeance: "", statut: "a_faire", notes: "" });
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  const [employees, setEmployees] = useState([]);
  const adminEmployees = employees.filter(e => e.est_actif !== false && (e.type_employe === 'admin' || e.est_proprietaire));
  const [questions, setQuestions] = useState(projet.questions_client || []);
  const [newQuestion, setNewQuestion] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/employees`).then(r => r.data).then(d => setEmployees(d.employees || [])).catch(() => {});
  }, []);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const addQuestion = async () => {
    if (!newQuestion.trim()) return;
    const updated = [...questions, { id: Date.now().toString(), texte: newQuestion.trim(), reponse: '', notes: '' }];
    setQuestions(updated);
    setNewQuestion('');
    await axios.put(`${API_URL}/api/projets-envergure/${projet.id}`, { questions_client: updated }, { headers });
  };
  const updateQuestion = async (qId, field, value) => {
    const updated = questions.map(q => q.id === qId ? { ...q, [field]: value } : q);
    setQuestions(updated);
    await axios.put(`${API_URL}/api/projets-envergure/${projet.id}`, { questions_client: updated }, { headers });
  };
  const deleteQuestion = async (qId) => {
    const updated = questions.filter(q => q.id !== qId);
    setQuestions(updated);
    await axios.put(`${API_URL}/api/projets-envergure/${projet.id}`, { questions_client: updated }, { headers });
  };



  const reload = async () => {
    const res = await axios.get(`${API_URL}/api/projets-envergure/${projet.id}`, { headers });
    if (res.status === 200) setProjet(res.data);
  };

  const saveInfo = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/projets-envergure/${projet.id}`, { ...infoForm, valeur_estimee: parseFloat(infoForm.valeur_estimee)
      });
      if (res.status === 200) { setProjet(res.data); setEditInfo(false); toast.success("Projet mis à jour"); }
    } finally { setSaving(false); }
  };

  const addEtape = async () => {
    if (!etapeForm.titre) { toast.error("Titre requis"); return; }
    const res = await axios.post(`${API_URL}/api/projets-envergure/${projet.id}/etapes`, etapeForm, { headers });
    if (res.status === 200) { setProjet(res.data); setNewEtape(false); setEtapeForm({ titre: "", description: "", responsable_nom: "", echeance: "", statut: "a_faire", notes: "" }); }
  };

  const updateEtape = async (etapeId, data) => {
    const res = await axios.put(`${API_URL}/api/projets-envergure/${projet.id}/etapes/${etapeId}`, data, { headers });
    if (res.status === 200) setProjet(res.data);
  };

  const deleteEtape = async (etapeId) => {
    if (!window.confirm("Supprimer cette étape ?")) return;
    const res = await axios.delete(`${API_URL}/api/projets-envergure/${projet.id}/etapes/${etapeId}`, { headers });
    if (res.status === 200) setProjet(prev => ({ ...prev, etapes: prev.etapes.filter(e => e.id !== etapeId) }));
  };

  const uploadFile = async (file) => {
    setUploadingFile(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await axios.post(`${API_URL}/api/projets-envergure/${projet.id}/fichiers`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.status === 200) { await reload(); toast.success("Fichier ajouté"); }
      else toast.error("Erreur lors du téléversement");
    } finally { setUploadingFile(false); }
  };

  const deleteFichier = async (fichier_id) => {
    const res = await axios.delete(`${API_URL}/api/projets-envergure/${projet.id}/fichiers/${fichier_id}`, { headers });
    if (res.status === 200) setProjet(prev => ({ ...prev, fichiers: prev.fichiers.filter(f => f.id !== fichier_id) }));
  };

  const st = STATUTS_PROJET[projet.statut] || STATUTS_PROJET.en_preparation;
  const doneCount = (projet.etapes || []).filter(e => e.statut === "complete").length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-list-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Retour
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 truncate">{projet.titre}</h1>
            <Badge className={`text-xs ${st.color}`}>{st.label}</Badge>
          </div>
          {projet.client_nom && <p className="text-sm text-slate-500">{projet.client_nom}</p>}
        </div>
        <Button variant="outline" size="sm" onClick={() => setEditInfo(true)} data-testid="edit-projet-btn">
          <Edit3 className="w-4 h-4 mr-1" /> Modifier
        </Button>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-indigo-600">{doneCount}/{(projet.etapes||[]).length}</p>
          <p className="text-xs text-slate-500">Étapes complétées</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-green-600">{fmt$(projet.valeur_estimee)}</p>
          <p className="text-xs text-slate-500">Valeur estimée</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-slate-700">{(projet.fichiers||[]).length}</p>
          <p className="text-xs text-slate-500">Fichiers</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-2xl font-bold text-amber-600">
            {(projet.etapes||[]).filter(e => e.statut === 'bloque').length}
          </p>
          <p className="text-xs text-slate-500">Bloquées</p>
        </CardContent></Card>
      </div>

      {/* Notes générales */}
      {projet.notes_generales && (
        <Card><CardContent className="p-4">
          <p className="text-sm font-semibold text-slate-700 mb-1">Notes / Plan de match</p>
          <p className="text-sm text-slate-600 whitespace-pre-wrap">{projet.notes_generales}</p>
        </CardContent></Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Étapes */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Plan d'action — Étapes</CardTitle>
              <Button size="sm" variant="outline" onClick={() => setNewEtape(true)} data-testid="add-etape-btn">
                <Plus className="w-3 h-3 mr-1" /> Étape
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {(projet.etapes || []).length === 0 && !newEtape && (
              <p className="text-center text-sm text-slate-400 py-4">Aucune étape — Ajoutez les actions à poser</p>
            )}
            {(projet.etapes || []).map(etape => (
              <EtapeRow key={etape.id} etape={etape}
                onUpdate={data => updateEtape(etape.id, data)}
                onDelete={id => deleteEtape(id)}
                adminEmployees={adminEmployees} />
            ))}
            {newEtape && (
              <div className="border-2 border-dashed border-indigo-300 rounded-lg p-3 space-y-2 bg-indigo-50/30">
                <Input placeholder="Titre de l'étape *" value={etapeForm.titre}
                  onChange={e => setEtapeForm(f => ({...f, titre: e.target.value}))}
                  data-testid="new-etape-titre" />
                <Textarea placeholder="Description..." rows={2} value={etapeForm.description}
                  onChange={e => setEtapeForm(f => ({...f, description: e.target.value}))} className="text-sm" />
                <div className="grid grid-cols-2 gap-2">
                  <Select value={etapeForm.responsable_id || 'none'} onValueChange={v => {
                    const emp = employees.find(e => e.id === v);
                    setEtapeForm(f => ({...f, responsable_id: v === 'none' ? '' : v, responsable_nom: emp ? `${emp.prenom} ${emp.nom}` : ''}));
                  }}>
                    <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Responsable" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {adminEmployees.map(emp => (
                        <SelectItem key={emp.id} value={emp.id}>{emp.prenom} {emp.nom}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="date" value={etapeForm.echeance}
                    onChange={e => setEtapeForm(f => ({...f, echeance: e.target.value}))} className="text-sm" />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={addEtape} data-testid="confirm-add-etape-btn">Ajouter</Button>
                  <Button size="sm" variant="ghost" onClick={() => setNewEtape(false)}>Annuler</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Questions à poser au client */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <HelpCircle className="w-4 h-4 text-purple-600" /> Questions à poser au client
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {questions.map(q => (
              <div key={q.id} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-slate-800 flex-1">{q.texte}</p>
                  <button onClick={() => deleteQuestion(q.id)} className="text-red-400 hover:text-red-600 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <div className="flex items-center gap-2">
                  {['Oui', 'Non', 'Peut-être'].map(opt => (
                    <button key={opt} onClick={() => updateQuestion(q.id, 'reponse', opt)}
                      className={`text-xs px-3 py-1 rounded-full border transition-colors ${q.reponse === opt 
                        ? opt === 'Oui' ? 'bg-emerald-100 border-emerald-300 text-emerald-700' 
                        : opt === 'Non' ? 'bg-red-100 border-red-300 text-red-700' 
                        : 'bg-amber-100 border-amber-300 text-amber-700'
                        : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                      {opt}
                    </button>
                  ))}
                </div>
                <input type="text" value={q.notes || ''} onChange={e => updateQuestion(q.id, 'notes', e.target.value)}
                  placeholder="Notes..." className="w-full text-xs border rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-purple-300" />
              </div>
            ))}
            <div className="flex gap-2">
              <input type="text" value={newQuestion} onChange={e => setNewQuestion(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addQuestion()}
                placeholder="Nouvelle question..." className="flex-1 text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-purple-300" />
              <Button size="sm" onClick={addQuestion} disabled={!newQuestion.trim()}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Fichiers */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Documents & Photos</CardTitle>
              <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile} data-testid="upload-fichier-btn">
                {uploadingFile ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                Ajouter
              </Button>
              <input ref={fileInputRef} type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                onChange={e => e.target.files?.[0] && uploadFile(e.target.files[0])} />
            </div>
          </CardHeader>
          <CardContent>
            {(projet.fichiers || []).length === 0 && (
              <div className="text-center py-6 text-slate-400">
                <Paperclip className="w-8 h-8 mx-auto mb-2 text-slate-200" />
                <p className="text-sm">Aucun fichier</p>
                <p className="text-xs mt-1">Photos, permis, soumissions, plans...</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              {(projet.fichiers || []).map(f => (
                <div key={f.id} className="relative group border rounded-lg overflow-hidden">
                  {f.type === "image" ? (
                    <a href={urlFichier(`/api${f.url}`)} target="_blank" rel="noreferrer">
                      <img src={urlFichier(`/api${f.url}`)} alt={f.nom}
                        className="w-full h-24 object-cover hover:opacity-90 transition-opacity" />
                    </a>
                  ) : (
                    <a href={urlFichier(`/api${f.url}`)} target="_blank" rel="noreferrer"
                      className="flex flex-col items-center justify-center h-24 bg-slate-50 hover:bg-slate-100 transition-colors">
                      <FileText className="w-8 h-8 text-slate-400" />
                      <p className="text-[10px] text-slate-500 truncate px-1 mt-1">{f.nom}</p>
                    </a>
                  )}
                  <button onClick={() => deleteFichier(f.id)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-3 h-3" />
                  </button>
                  {f.type === "image" && (
                    <p className="text-[10px] text-slate-500 truncate px-2 pb-1 text-center">{f.nom}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modal modifier infos projet */}
      <Dialog open={editInfo} onOpenChange={setEditInfo}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Modifier le projet</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Titre du projet *</Label>
              <Input value={infoForm.titre} onChange={e => setInfoForm(f => ({...f, titre: e.target.value}))}
                data-testid="edit-titre-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Client / Promoteur</Label>
                <Input value={infoForm.client_nom} onChange={e => setInfoForm(f => ({...f, client_nom: e.target.value}))} />
              </div>
              <div>
                <Label>Valeur estimée ($)</Label>
                <Input type="number" value={infoForm.valeur_estimee}
                  onChange={e => setInfoForm(f => ({...f, valeur_estimee: e.target.value}))} />
              </div>
            </div>
            <div>
              <Label>Adresse</Label>
              <Input value={infoForm.adresse} onChange={e => setInfoForm(f => ({...f, adresse: e.target.value}))} />
            </div>
            <div>
              <Label>Statut</Label>
              <Select value={infoForm.statut} onValueChange={v => setInfoForm(f => ({...f, statut: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUTS_PROJET).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes / Plan de match</Label>
              <Textarea rows={4} value={infoForm.notes_generales}
                onChange={e => setInfoForm(f => ({...f, notes_generales: e.target.value}))}
                placeholder="Décrivez la stratégie, les étapes importantes, les contraintes..." />
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={saveInfo} disabled={saving} className="flex-1" data-testid="save-projet-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Sauvegarder
              </Button>
              <Button variant="ghost" onClick={() => setEditInfo(false)}>Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────
export function ProjetsEnvergure() {
  const { token, user } = useAuth();
  const [projets, setProjets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProjet, setSelectedProjet] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ titre: "", client_nom: "", valeur_estimee: "", statut: "en_preparation", notes_generales: "" });
  const [creating, setCreating] = useState(false);
  const [filterStatut, setFilterStatut] = useState("tous");

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const fetchProjets = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/projets-envergure`, { headers });
      if (res.status === 200) { const d = res.data; setProjets(d.projets || []); }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchProjets(); }, []);

  const createProjet = async () => {
    if (!createForm.titre) { toast.error("Titre requis"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/projets-envergure`, { ...createForm, valeur_estimee: parseFloat(createForm.valeur_estimee)
      });
      if (res.status === 200) {
        const p = res.data;
        setProjets(prev => [p, ...prev]);
        setSelectedProjet(p);
        setShowCreate(false);
        setCreateForm({ titre: "", client_nom: "", valeur_estimee: "", statut: "en_preparation", notes_generales: "" });
        toast.success("Projet créé !");
      }
    } finally { setCreating(false); }
  };

  const deleteProjet = async (id) => {
    if (!window.confirm("Supprimer ce projet ?")) return;
    const res = await axios.delete(`${API_URL}/api/projets-envergure/${id}`, { headers });
    if (res.status === 200) {
      setProjets(prev => prev.filter(p => p.id !== id));
      if (selectedProjet?.id === id) setSelectedProjet(null);
      toast.success("Projet supprimé");
    }
  };

  const filtered = filterStatut === "tous" ? projets : projets.filter(p => p.statut === filterStatut);

  if (selectedProjet) {
    return (
      <div className="p-4 md:p-6 max-w-5xl mx-auto">
        <ProjetDetail projet={selectedProjet} onBack={() => { setSelectedProjet(null); fetchProjets(); }} token={token} />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
            <Star className="w-6 h-6 text-indigo-500" /> Projets d'envergure
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Préparation des soumissions pour grands projets (100 000 $ +)</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="create-projet-btn">
          <Plus className="w-4 h-4 mr-1" /> Nouveau projet
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[{ key: "tous", label: "Tous" }, ...Object.entries(STATUTS_PROJET).map(([k, v]) => ({ key: k, label: v.label }))].map(s => {
          const count = s.key === "tous" ? projets.length : projets.filter(p => p.statut === s.key).length;
          return (
            <button key={s.key} onClick={() => setFilterStatut(s.key)}
              className={`rounded-lg p-2 text-center text-xs font-medium transition-all border ${filterStatut === s.key ? "bg-indigo-600 text-white border-indigo-600" : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-indigo-300"}`}>
              <span className="text-lg font-bold block">{count}</span>
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-indigo-500" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Star className="w-12 h-12 mx-auto mb-3 text-indigo-200" />
          <p className="font-medium">Aucun projet d'envergure</p>
          <p className="text-sm mt-1">Créez votre premier projet pour préparer une soumission importante</p>
          <Button onClick={() => setShowCreate(true)} className="mt-4 bg-indigo-600 hover:bg-indigo-700">
            <Plus className="w-4 h-4 mr-1" /> Créer un projet
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(p => (
            <ProjetCard key={p.id} projet={p} onClick={setSelectedProjet} onDelete={deleteProjet} />
          ))}
        </div>
      )}

      {/* Modal créer */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nouveau projet d'envergure</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Titre du projet *</Label>
              <Input placeholder="Ex: Résidence Tremblay — Rénovation complète"
                value={createForm.titre} onChange={e => setCreateForm(f => ({...f, titre: e.target.value}))}
                data-testid="create-titre-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Client / Promoteur</Label>
                <Input placeholder="Nom du client" value={createForm.client_nom}
                  onChange={e => setCreateForm(f => ({...f, client_nom: e.target.value}))} />
              </div>
              <div>
                <Label>Valeur estimée ($)</Label>
                <Input type="number" placeholder="250000" value={createForm.valeur_estimee}
                  onChange={e => setCreateForm(f => ({...f, valeur_estimee: e.target.value}))} />
              </div>
            </div>
            <div>
              <Label>Notes initiales</Label>
              <Textarea rows={3} placeholder="Description, défis anticipés, étapes importantes..."
                value={createForm.notes_generales}
                onChange={e => setCreateForm(f => ({...f, notes_generales: e.target.value}))} />
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={createProjet} disabled={creating} className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                data-testid="confirm-create-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Créer
              </Button>
              <Button variant="ghost" onClick={() => setShowCreate(false)}>Annuler</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ProjetsEnvergure;
